/* ============================================================
   Service Worker Registration — PRASI LABS CRM
   Call register() in index.js to enable PWA features.
   Call unregister() to disable (e.g., during development).
   ============================================================ */

const SW_URL = `${process.env.PUBLIC_URL}/service-worker.js`;

export function register(config = {}) {
  const isProd = process.env.NODE_ENV === 'production';
  const swEnabled = process.env.REACT_APP_ENABLE_SW === 'true';
  if (!isProd && !swEnabled) return;
  if (!('serviceWorker' in navigator)) return;

  window.addEventListener('load', () => {
    checkValidServiceWorker(SW_URL, config);
  });
}

async function checkValidServiceWorker(swUrl, config) {
  try {
    const response = await fetch(swUrl, { headers: { 'Service-Worker': 'script' } });
    const contentType = response.headers.get('content-type');

    if (response.status === 404 || (contentType && !contentType.includes('javascript'))) {
      // SW file not found or returned wrong content — unregister if one exists.
      // Use getRegistration() (not .ready) to avoid hanging forever when no SW
      // is registered, which would cause an unresolvable promise and no reload.
      const reg = await navigator.serviceWorker.getRegistration(swUrl);
      if (reg) {
        await reg.unregister();
        window.location.reload();
      }
      return;
    }

    registerValidSW(swUrl, config);
  } catch {
    console.log('No internet connection. App running in offline mode.');
  }
}

async function registerValidSW(swUrl, config) {
  try {
    // Capture BEFORE registration so we know if a SW was already in control.
    // If there was no previous SW (first install), clients.claim() in the SW's
    // activate event fires controllerchange — but that must NOT trigger a reload
    // because there is nothing stale to replace. Only update flows (old SW → new SW)
    // should reload.
    const hadPreviousController = !!navigator.serviceWorker.controller;

    const registration = await navigator.serviceWorker.register(swUrl);

    registration.onupdatefound = () => {
      const installingWorker = registration.installing;
      if (!installingWorker) return;

      installingWorker.onstatechange = () => {
        if (installingWorker.state === 'installed') {
          if (navigator.serviceWorker.controller) {
            // A previous SW was in control — new version is ready and waiting.
            console.log('New app version available. Refresh to update.');
            if (config.onUpdate) config.onUpdate(registration);
          } else {
            // First install — no previous SW. App is now cached for offline use.
            console.log('App cached for offline use.');
            if (config.onSuccess) config.onSuccess(registration);
          }
        }
      };
    };

    // Only reload when an UPDATE takes control (old SW → new SW via SKIP_WAITING).
    // Ignore the controllerchange fired by clients.claim() on first install.
    let reloading = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!hadPreviousController) return; // first install — nothing stale, no reload needed
      if (reloading) return;
      reloading = true;
      window.location.reload();
    });
  } catch (error) {
    console.error('Service worker registration failed:', error);
  }
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    // Use getRegistrations() instead of .ready so we unregister ALL workers
    // immediately without waiting for an active SW to resolve first.
    // .ready can lose the race against a SW that fires controllerchange → reload
    // before the promise settles.
    navigator.serviceWorker.getRegistrations()
      .then(regs => regs.forEach(reg => reg.unregister()))
      .catch(console.error);
  }
}
